/**
 * MappingHistoryModal — staff-facing list of recent mapping versions
 * for a client, with a one-click revert.
 *
 * Added 2026-05-21 as part of the P0 mapping-wipe incident response.
 * See /app/memory/mapping_forensics/POSTMORTEM.md for context.
 *
 * Design notes:
 *   - Read-only list of timestamped versions (most recent first).
 *   - Each row shows: when archived, who triggered the save that
 *     replaced it, and the declared origin of that save (e.g.
 *     `staff-coa-editor`, `admin-revert`).
 *   - The "Revert" action calls POST /api/mappings/<cid>/revert/<hid>
 *     which itself archives the current state before swapping in
 *     the chosen historical version — a revert is never a one-way
 *     door.
 *   - We do NOT show a structural diff in this first iteration
 *     because the canonical mapping is JSON with ~hundreds of
 *     nodes and a useful diff renderer is its own milestone. The
 *     revert action is the load-bearing feature; staff can preview
 *     by reverting, comparing the rendered statements, then
 *     reverting forward again if needed.
 */
import { useEffect, useState } from 'react';
import { Loader2, History, RotateCcw, X } from 'lucide-react';

const COLORS = {
  primary: '#0085CA',
  border: '#E5E7EB',
  muted: '#6B7280',
  danger: '#DC2626',
};

export default function MappingHistoryModal({ clientId, clientName, onClose, onReverted }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revertingId, setRevertingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('smj_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/mappings/${clientId}/history?limit=50`, { headers: authHeaders });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setEntries(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const revert = async (historyId) => {
    setRevertingId(historyId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/mappings/${clientId}/revert/${historyId}`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        // V25.9 — 409 = the version belongs to a different QuickBooks company
        // file. Surface the server's explanation verbatim rather than "HTTP 409".
        let detail = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch { /* non-JSON body */ }
        throw new Error(detail);
      }
      if (onReverted) onReverted();
      onClose();
    } catch (e) {
      setError(`Revert failed: ${e.message}`);
    } finally {
      setRevertingId(null);
      setConfirmId(null);
    }
  };

  return (
    <div
      data-testid="mapping-history-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, padding: 24,
          maxWidth: 720, width: '100%', maxHeight: '85vh',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 18 }}>
            <History size={20} color={COLORS.primary} />
            Mapping History — {clientName || clientId}
          </h3>
          <button
            data-testid="mapping-history-close"
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.muted }}
          >
            <X size={20} />
          </button>
        </div>

        <p style={{ margin: 0, fontSize: 13, color: COLORS.muted, lineHeight: 1.5 }}>
          Every save to this client's Chart of Accounts mapping archives the prior version below.
          You can revert to any prior state with one click; the act of reverting itself archives
          the current state, so this is never a one-way door.
        </p>

        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.muted }}>
            <Loader2 size={20} className="animate-spin" /> Loading history…
          </div>
        )}
        {error && (
          <div data-testid="mapping-history-error" style={{ padding: 12, background: '#FEE', color: COLORS.danger, borderRadius: 6, fontSize: 13 }}>
            {error}
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: COLORS.muted, fontSize: 14 }}>
            No prior versions yet. History begins recording from the next save.
          </div>
        )}

        {!loading && entries.length > 0 && (
          <div style={{ flex: 1, overflowY: 'auto', borderTop: `1px solid ${COLORS.border}` }}>
            {entries.map((e) => (
              <div
                key={e.id}
                data-testid={`mapping-history-entry-${e.id}`}
                style={{
                  padding: '14px 0', borderBottom: `1px solid ${COLORS.border}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    Saved {fmtDate(e.saved_at)}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                    Archived {fmtDate(e.archived_at)} · replaced by save from {e.archived_because_of_save_origin || 'unknown origin'}
                    {e.archived_because_of_save_by && ` (user ${e.archived_because_of_save_by.slice(0, 8)})`}
                  </div>
                </div>
                {confirmId === e.id ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      data-testid={`mapping-history-confirm-revert-${e.id}`}
                      disabled={revertingId === e.id}
                      onClick={() => revert(e.id)}
                      style={btnStyle(COLORS.danger)}
                    >
                      {revertingId === e.id ? <Loader2 size={14} className="animate-spin" /> : 'Confirm revert'}
                    </button>
                    <button onClick={() => setConfirmId(null)} style={btnStyle('#9CA3AF')}>Cancel</button>
                  </div>
                ) : (
                  <button
                    data-testid={`mapping-history-revert-${e.id}`}
                    onClick={() => setConfirmId(e.id)}
                    style={{
                      ...btnStyle(COLORS.primary),
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <RotateCcw size={13} /> Revert
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function btnStyle(bg) {
  return {
    padding: '6px 12px', background: bg, color: '#fff',
    border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  };
}
